/* Kada je dokument učitan — čita se IZ NAZIVA FAJLA.
 *
 * `TMS_Backend/middleware/upload.js` svakom otpremljenom fajlu dopisuje vremensku
 * oznaku pre ekstenzije:
 *
 *     safeFileName()  ->  `${baseName}_${timestamp}${ext}`
 *     timestamp       =  new Date().toISOString().replace(/[-:.TZ]/g, "")
 *
 * pa „ugovor.pdf" otpremljen 02.03.2026. u 09:04:45.292 UTC postane
 * `ugovor_20260302090445292.pdf` — 17 cifara, YYYYMMDDHHMMSSmmm.
 *
 * ── ZAŠTO IZ NAZIVA, A NE ODNEKUD DRUGDE ────────────────────────────────────
 * Razmotreno i odbačeno:
 *   • vreme izmene fajla na disku — menja ga svaka kopija, bekap ili seoba
 *     servera, pa bi posle prve selidbe svi dokumenti imali isti datum;
 *   • `DateEdited` na redu u bazi — menja se pri svakoj izmeni vozila, ne nosi
 *     ništa o pojedinačnom dokumentu, a jedan red drži i do šest dokumenata;
 *   • nova kolona sa datumom po dokumentu — tražila bi po jednu uz svaku od 13
 *     kolona sa putanjama, a i dalje ne bi važila za postojeće fajlove.
 * Naziv je jedini izvor koji je TAČAN i za sve što je već otpremljeno.
 *
 * Prava popravka je zasebna tabela dokumenata (`id_vozilo, vrsta, putanja,
 * user_id, DateEntered`) — ona bi nosila i KO je otpremio, i uklonila spajanje
 * više putanja u jedan string znakom `|`. To je veći zahvat; dok ga nema, naziv
 * daje tačan odgovor bez ijedne izmene šeme.
 *
 * ── VREMENSKA ZONA ──────────────────────────────────────────────────────────
 * `toISOString()` je UTC. Prikaz zato MORA kroz zonu, inače je leti dva sata
 * pomeren — fajl otpremljen u 01:30 po Beogradu pokazao bi prethodni dan.
 */

const ZONA = 'Europe/Belgrade';

/* 17 cifara neposredno pre ekstenzije. Namerno strogo: `\d{17}` i kraj naziva,
   da se ne uhvati broj koji je korisnik sam imao u imenu fajla. */
const OBRAZAC = /_(\d{17})\.[^.\\/]+$/;

/** Jedna putanja -> trenutak otpremanja u ms, ili null ako naziv nema oznaku. */
export function vremeIzPutanje(putanja) {
    const m = OBRAZAC.exec(String(putanja || ''));
    if (!m) return null;
    const d = m[1];
    /* Date.UTC, jer je oznaka zapisana iz toISOString() — dakle UTC. */
    const ms = Date.UTC(
        +d.slice(0, 4), +d.slice(4, 6) - 1, +d.slice(6, 8),
        +d.slice(8, 10), +d.slice(10, 12), +d.slice(12, 14), +d.slice(14, 17),
    );
    return Number.isFinite(ms) ? ms : null;
}

/**
 * Najnoviji trenutak otpremanja iz polja koje ume da drži VIŠE putanja
 * razdvojenih znakom `|` (npr. AO osiguranje). Uzima se najnoviji, jer novi
 * upload se nadovezuje — pitanje „kad je učitan" odnosi se na poslednji.
 */
export function vremeDokumenta(vrednost) {
    if (!vrednost || typeof vrednost !== 'string') return null;
    let najnoviji = null;
    for (const deo of vrednost.split('|')) {
        const ms = vremeIzPutanje(deo);
        if (ms != null && (najnoviji == null || ms > najnoviji)) najnoviji = ms;
    }
    return najnoviji;
}

/** „02.03.2026. 10:04" po zoni, ili prazno kad oznake nema. */
export function fmtDatumFajla(vrednost) {
    const ms = vremeDokumenta(vrednost);
    if (ms == null) return '';
    try {
        const p = new Intl.DateTimeFormat('sr-RS', {
            timeZone: ZONA, hour12: false,
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        }).formatToParts(new Date(ms)).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
        return `${p.day}.${p.month}.${p.year}. ${p.hour}:${p.minute}`;
    } catch {
        return '';
    }
}

/**
 * Natpis dugmeta kad dokument već stoji u bazi. Datum ide U SAM NATPIS, a ne
 * pored njega: dugme je jedino mesto na koje korisnik i gleda kad proverava da
 * li je fajl tu, a forma ima 13 ovakvih polja — zaseban red uz svako bi
 * razvukao unos bez ijedne nove informacije.
 *
 * Fajl bez vremenske oznake (otpremljen pre uvođenja ovog naziva) zadržava
 * stari natpis — bolje ništa nego pogrešan datum.
 */
export function natpisFajlaUBazi(vrednost, osnovni = 'Fajl u bazi!') {
    const d = fmtDatumFajla(vrednost);
    return d ? `${osnovni} · ${d}` : osnovni;
}
